import { CarouselProduct } from '../carouselProduct'
import { postBeaconTracking, redirect, generateBeaconClick, generateBeaconProductClick } from '../../helpers/searchspring-tracking'

export type CarouselCardVariant = 'default' | 'compact'

type Props = {
  product: CarouselProduct
  profile?: string
  sku?: string
  variant?: CarouselCardVariant
}

const styles = {
  default: {
    image: 'aspect-[170/212] lg:aspect-[335/419]',
    title: 'text-[12px] md:text-[14px] leading-[150%] md:leading-[120%]',
    price: 'text-[12px]',
    priceRow: 'flex',
    showBiLine: true
  },
  compact: {
    image: 'aspect-[170/212]',
    title: 'text-[9px] md:text-[12px] leading-[120%]',
    price: 'text-[12px]',
    priceRow: 'flex',
    showBiLine: false
  }
}

const CarouselCard = ({ product, profile = '', variant = 'default' }: Props): JSX.Element | null => {
  if (!product) return null

  const style = styles[variant]
  const biLine = style.showBiLine ? product.biLine : undefined

  const handleClickTracking = async (e: any) => {
    /* Only Searchspring results are tracked - everything else is a plain link. */
    if (!product.raw) return

    e.preventDefault()
    const beaconClickTracking = [
      generateBeaconClick(product.raw, profile, 'profile.click'),
      generateBeaconProductClick(product.raw, profile, 'profile.product.click', null)
    ]
    const result = await postBeaconTracking(beaconClickTracking)

    if (result) {
      window.location.href = product.url
    }

    redirect(product.url, e.ctrlKey)
  }

  const price = (
    <div className={`flex justify-start gap-1 font-calibre uppercase tracking-[4%] leading-[120%] ${style.price}`}>
      <p className="font-light">{product.priceText}</p>
      {product.compareAtText && <p className="line-through opacity-40">{product.compareAtText}</p>}
    </div>
  )

  return (
    <div className="group relative">
      <a href={product.url} className="block relative w-full h-auto" title={product.title} onClick={handleClickTracking}>
        <img src={product.image} width="auto" height="auto" alt={product.title} className={`w-full h-auto object-cover ${style.image}`} loading="lazy" />
        {product.imageHover && (
          <img
            src={product.imageHover}
            width="auto"
            height="auto"
            alt={`${product.title} hover`}
            loading="lazy"
            className={`w-full h-auto absolute top-0 left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[350ms] ease-in-out md-max:hidden object-cover ${style.image}`}
          />
        )}
      </a>

      <div className="relative flex flex-col">
        <div className={`flex flex-col mt-XS transition-opacity duration-[350ms] ease-in-out ${biLine ? 'md:group-hover:opacity-0' : ''}`}>
          <a href={product.url} onClick={handleClickTracking}>
            <h3 className={`uppercase font-bstSpyre tracking-[1%] text-base-2 ${style.title}`}>{product.title}</h3>
          </a>
          <div className={`mt-XS justify-between items-center gap-2.5 ${style.priceRow}`}>{price}</div>
        </div>

        {biLine && (
          <div className="absolute top-0 left-0 w-full transition-opacity duration-[350ms] ease-in-out opacity-0 group-hover:opacity-100 md-max:hidden">
            <div className="flex align-baseline justify-between gap-2.5 w-full mt-XS">
              <a href={product.url} onClick={handleClickTracking}>
                <h3 className="font-bstSpyre text-base-2">{biLine}</h3>
              </a>
            </div>
            <div className="mt-XS">{price}</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CarouselCard
